import { Card, Icon, Portal } from '@chakra-ui/react';
import {
  IAccountRespSchema,
  IOfferingRes,
  IPriceTierSettingsReq,
  IPriceUnderProductSchemaUI,
  IQuoteItemWithDisplayType,
  IQuoteOfferingRespSchema,
  IRateResSchema,
  OfferingTypesEnum,
  PriceModelEnum,
  ProductQuantityDisplayTypeEnum,
  RateTypeEnum,
  SelectedProductsWithinOfferingForm,
} from '@monetize/types/app';
import { IOrderedObj } from '@monetize/types/common';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { CurrencyIcon } from '@monetize/ui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { useGetQuoteOfferingRateEditability } from '../../../api/quoteOfferingRateService';
import { useExperienceConfiguration, useOfferingRate } from '../../../hooks';
import { ManageProductsFormWithCard } from '../../../routes/Quotes/Quote/components/quoteOffering/manageProducts/ManageProductsForm';
import { useConfirmModal } from '../../../services/confirmModal';
import {
  CONFIRM_CLOSE_UNSAVED_DESC,
  CONFIRM_CLOSE_UNSAVED_TITLE,
  CONFIRM_SWITCH_UNSAVED_DESC,
  CONFIRM_TAB_SWITCH_TITLE,
} from '../../../utils/messages';
import { arrayToObjectOrdered } from '../../../utils/misc';
import {
  MBox,
  MButton,
  MCard,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MFlex,
  MPageLoader,
  MTab,
  MTabList,
  MTabPanel,
  MTabPanels,
  MTabs,
} from '../../Monetize';
import { PricesDrawerBodyEdit } from '../PricesDrawer/PricesDrawerBodyEdit';
import { PricesDrawerBodyView } from '../PricesDrawer/PricesDrawerBodyView';
import { QuotePriceTierDisplaySettings } from '../QuotePriceTierDisplaySettings/QuotePriceTierDisplaySettings';

interface QuoteDrawerProps {
  offering: IOfferingRes;
  account: IAccountRespSchema | Pick<IAccountRespSchema, 'id' | 'accountName'>;
  quoteOffering: IQuoteOfferingRespSchema;
  offeringRate: IRateResSchema | null;
  orderedPricesUnderProduct: IPriceUnderProductSchemaUI[];
  offeringType: OfferingTypesEnum;
  allowPriceCustomization: boolean;
  isDisabledButton: boolean;
  disabled?: boolean;
  isOfferingOpen: boolean;
  onRateSaved: (rate: IRateResSchema) => Promise<void> | void;
  onRateEdited?: (rate: IRateResSchema) => Promise<void> | void;
  setOfferingRate: (val: IRateResSchema | null) => void;
  setOrderedPricesUnderProduct: (val: IPriceUnderProductSchemaUI[]) => void;
  onDrawerToggle: (isOpen: boolean) => void;
  modalConfig: {
    isOpen: boolean;
    onClose: () => void;
    onOpen: () => void;
  };
  updateOfferingItems: (
    offering: SelectedProductsWithinOfferingForm,
  ) => Promise<void>;
  isQuoteOfferingProcessing: boolean;
  areProductsOptional: boolean;
  /** Onetime charges cannot be added to an offering carried over from the amended contract */
  disableOneTimeProducts?: boolean;
  setProductsLoading: (val: boolean) => void;
  isProductsLoading: boolean;
  savePriceTiers: ({
    priceDisplayMode,
    displayedPriceIds,
  }: IPriceTierSettingsReq) => void;
}

export const QuoteDrawer = ({
  offering,
  account,
  quoteOffering,
  orderedPricesUnderProduct,
  offeringRate,
  allowPriceCustomization,
  isDisabledButton,
  disabled,
  isOfferingOpen,
  offeringType,
  onRateSaved,
  onRateEdited,
  setOfferingRate,
  setOrderedPricesUnderProduct,
  onDrawerToggle,
  modalConfig,
  updateOfferingItems,
  isQuoteOfferingProcessing,
  areProductsOptional,
  disableOneTimeProducts,
  setProductsLoading,
  isProductsLoading,
  savePriceTiers,
}: QuoteDrawerProps) => {
  const xhrController = useRef(new AbortController());
  const pricesBtnRef = useRef<any>(undefined);
  const { isOpen, onOpen, onClose } = modalConfig;
  const [tabIndex, setTabIndex] = useState(1);
  const { enableQuoteOfferingPriceTiers } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );

  const [productsObj, setProductsObj] = useState<
    IOrderedObj<IQuoteItemWithDisplayType>
  >({
    byId: {},
    orderedIds: [],
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [rateEditMode, setRateEditMode] = useState<'clone' | 'edit'>('clone');
  const [isProductListChanged, setIsProductListChanged] = useState(false);
  const [isPriceTierUpdated, setIsPriceTierUpdated] = useState(false);
  const [isPriceTierLoading, setIsPriceTierLoading] = useState(false);
  const [isSavingPriceTier, setIsSavingPriceTier] = useState(false);
  const [isRateSaving, setIsRateSaving] = useState(false);

  const { createOfferingRate } = useOfferingRate();

  const handleEditModeChange = useCallback((val: boolean) => {
    setIsEditMode(val);
    if (!val) {
      setRateEditMode('clone');
    }
  }, []);

  const { data: rateEditability } = useGetQuoteOfferingRateEditability(
    quoteOffering.quoteId,
    quoteOffering.id,
    quoteOffering.rateId,
    {
      enabled: allowPriceCustomization && !!quoteOffering.rateId,
    },
  );

  const editabilityForRate =
    rateEditability &&
    (!offeringRate ||
      !rateEditability.rateId ||
      rateEditability.rateId === offeringRate.id)
      ? rateEditability
      : undefined;

  const { showConfirmModal, closeModal: closeConfirmationModal } =
    useConfirmModal();

  const offeringName = offeringRate?.offering?.name || '';

  const getSelectedOrderedPricesUnderProduct = (
    productData: IPriceUnderProductSchemaUI[],
  ) => {
    return productData.filter((rate) => {
      const { id } = rate;
      return quoteOffering?.items.some(({ productId }) => id === productId);
    });
  };

  const productsWithPrices: IPriceUnderProductSchemaUI[] =
    orderedPricesUnderProduct;

  const selectedProductsWithPrices: IPriceUnderProductSchemaUI[] =
    getSelectedOrderedPricesUnderProduct(orderedPricesUnderProduct);

  const priceTierLockEligibleProductIds = useMemo<ReadonlySet<string>>(() => {
    const selectedProductIds = new Set(
      quoteOffering.items.map(({ productId }) => productId),
    );
    return new Set(
      offering.offeringProducts
        .filter((offeringProduct) => offeringProduct.isPriceTierLockEligible)
        .filter((offeringProduct) =>
          selectedProductIds.has(offeringProduct.product.id),
        )
        .filter((offeringProduct) => {
          const productPrices = orderedPricesUnderProduct.find(
            ({ id }) => id === offeringProduct.product.id,
          );
          return !productPrices?.prices.some(
            ({ priceModel }) => priceModel === PriceModelEnum.CUSTOM,
          );
        })
        .map(({ product }) => product.id),
    );
  }, [
    offering.offeringProducts,
    orderedPricesUnderProduct,
    quoteOffering.items,
  ]);

  useEffect(() => {
    const { current } = xhrController;
    return () => {
      current?.abort();
    };
  }, []);

  useEffect(() => {
    if (
      quoteOffering &&
      quoteOffering?.items?.length > 0 &&
      offering &&
      offering?.offeringProducts?.length > 0
    ) {
      const { offeringProducts } = offering;
      const quoteOfferingItemsWithQuantityDisplayType = quoteOffering.items.map(
        (offeringItem) => {
          const quantityDisplayType = offeringProducts.find(
            (offeringProduct) =>
              offeringProduct.product.id === offeringItem.productId,
          )?.quantityDisplayType;

          return {
            ...offeringItem,
            quantityDisplayType,
          };
        },
      );

      setProductsObj(
        arrayToObjectOrdered(
          quoteOfferingItemsWithQuantityDisplayType.map((item) => ({
            ...item,
            quantityDisplayType:
              item.quantityDisplayType ?? ProductQuantityDisplayTypeEnum.INPUT,
          })),
          'productId',
        ),
      );
    }
  }, [quoteOffering, offering]);

  useEffect(() => {
    onDrawerToggle?.(isOpen);
    if (!isOpen) {
      setTabIndex(1);
    }
  }, [isOpen, onDrawerToggle]);

  async function handleClose(skipEditWarning = false, submitting = false) {
    if (isRateSaving) {
      return;
    }
    if (!skipEditWarning && isEditMode) {
      showConfirmModal({
        title: CONFIRM_CLOSE_UNSAVED_TITLE,
        description: CONFIRM_CLOSE_UNSAVED_DESC,
        onYes: () => {
          closeConfirmationModal();
          handleEditModeChange(false);
          onClose();
        },
        yesBtnProps: {
          variant: 'primary',
        },
        noBtnProps: {
          variant: 'cancel',
        },
      });
      setTabIndex(0);
    } else if (tabIndex === 0) {
      handleEditModeChange(false);

      if (!submitting) {
        onClose();
      }
    }

    if (!skipEditWarning && isProductListChanged) {
      showConfirmModal({
        title: CONFIRM_CLOSE_UNSAVED_TITLE,
        description: CONFIRM_CLOSE_UNSAVED_DESC,
        onYes: () => {
          closeConfirmationModal();
          setIsProductListChanged(false);
          onClose();
        },
        yesBtnProps: {
          variant: 'primary',
        },
        noBtnProps: {
          variant: 'cancel',
        },
      });
      setTabIndex(1);
    } else if (tabIndex === 1) {
      setIsProductListChanged(false);
      if (!submitting) {
        onClose();
      }
    }

    if (!skipEditWarning && isPriceTierUpdated) {
      showConfirmModal({
        title: CONFIRM_CLOSE_UNSAVED_TITLE,
        description: CONFIRM_CLOSE_UNSAVED_DESC,
        onYes: () => {
          closeConfirmationModal();
          setIsPriceTierUpdated(false);
          onClose();
        },
        yesBtnProps: {
          variant: 'primary',
        },
        noBtnProps: {
          variant: 'cancel',
        },
      });
      setTabIndex(2);
    } else if (tabIndex === 2) {
      setIsPriceTierUpdated(false);

      if (!submitting) {
        onClose();
      }
    }
  }

  const handleDrawerToggle = () => {
    setTabIndex(0);
    onOpen();
  };

  const handleTabSwitch = (newTabIndex: number) => {
    if (isEditMode || isProductListChanged) {
      showConfirmModal({
        title: CONFIRM_TAB_SWITCH_TITLE,
        description: CONFIRM_SWITCH_UNSAVED_DESC,
        onYes: () => {
          closeConfirmationModal();
          handleEditModeChange(false);
          setTabIndex(newTabIndex);
          setIsProductListChanged(false);
        },
        yesBtnProps: {
          variant: 'primary',
        },
        noBtnProps: {
          variant: 'cancel',
        },
      });
    } else {
      setTabIndex(newTabIndex);
    }
  };

  return (
    <MBox position="relative">
      <MButton
        leftIcon={<CurrencyIcon />}
        iconSpacing={isOfferingOpen ? 1 : 0}
        ref={pricesBtnRef}
        onClick={handleDrawerToggle}
        variant="tertiary"
        size="sm"
        isDisabled={isDisabledButton}
        data-testid="prices-btn"
      >
        {!!isOfferingOpen && 'Prices'}
      </MButton>

      {isOpen && (
        <MDrawer
          open={isOpen}
          size="lg"
          placement="end"
          onOpenChange={(e) => {
            if (!e.open) {
              handleClose();
            }
          }}
          finalFocusEl={tabIndex === 0 ? () => pricesBtnRef.current : undefined}
          preventScroll={false}
          closeOnInteractOutside={!isRateSaving}
          closeOnEscape={!isRateSaving}
        >
          <Portal>
            <MDrawerOverlay />
            <MDrawerPositioner>
              <MDrawerContent>
                <MDrawerCloseButton>
                  <Icon asChild>
                    <MdClose />
                  </Icon>
                </MDrawerCloseButton>
                <MDrawerHeader
                  fontSize="md"
                  borderBottomColor="tGray.back"
                  borderBottomWidth="1px"
                  borderBottomStyle="solid"
                >
                  {offeringName}
                </MDrawerHeader>

                <MDrawerBody p={0} backgroundColor="tGray.drawerSidebar">
                  <MTabs
                    mb={0}
                    variant="line"
                    size="sm"
                    defaultIndex={tabIndex}
                    index={tabIndex}
                    onChange={(newTabIndex) => handleTabSwitch(newTabIndex)}
                  >
                    <MTabList backgroundColor="tWhite.base" py={4} px={6}>
                      <MTab>Prices</MTab>
                      {areProductsOptional && <MTab>Products</MTab>}
                      {enableQuoteOfferingPriceTiers && (
                        <MTab>Price Tier Display</MTab>
                      )}
                    </MTabList>
                    <MTabPanels>
                      {/* PRICES */}
                      <MTabPanel>
                        <MCard variant="borderless" mb="2">
                          <Card.Body>
                            {!offeringRate && (
                              <MFlex justify="center" grow={1}>
                                <MPageLoader height="200px" />
                              </MFlex>
                            )}
                            {!allowPriceCustomization && (
                              <PricesDrawerBodyView
                                offeringType={offeringType}
                                currency={offeringRate?.currency || ''}
                                rateName={offeringRate?.name || ''}
                                internalDescription={
                                  offeringRate?.internalDescription
                                }
                                billingFrequency={
                                  offeringRate?.billingFrequency
                                }
                                billingFrequencyInMonths={
                                  offeringRate?.billingFrequencyInMonths
                                }
                                isCustomRate={
                                  offeringRate?.rateType ===
                                  RateTypeEnum.ACCOUNT
                                }
                                productsWithPrices={selectedProductsWithPrices}
                                productsObj={productsObj}
                              />
                            )}
                            {allowPriceCustomization &&
                              offeringRate &&
                              account && (
                                <MBox>
                                  {/* Allow editing rates if not custom or if it is custom is is not locked */}
                                  <PricesDrawerBodyEdit
                                    key={offeringRate.id}
                                    disabled={disabled}
                                    account={account}
                                    offeringRate={offeringRate}
                                    selectedProductsWithPrices={
                                      selectedProductsWithPrices
                                    }
                                    productsObj={productsObj}
                                    isEditMode={isEditMode}
                                    setIsEditMode={handleEditModeChange}
                                    rateEditMode={rateEditMode}
                                    setRateEditMode={setRateEditMode}
                                    editability={editabilityForRate}
                                    isEditabilityLoading={!editabilityForRate}
                                    onRateSaved={onRateSaved}
                                    onRateEdited={onRateEdited}
                                    createOfferingRate={createOfferingRate}
                                    setOfferingRate={setOfferingRate}
                                    setOrderedPricesUnderProduct={
                                      setOrderedPricesUnderProduct
                                    }
                                    onCancel={() => handleClose(false, false)}
                                    onSavingChange={setIsRateSaving}
                                    offeringType={offeringType}
                                    productsWithPrices={productsWithPrices}
                                    eligibleProductIds={
                                      priceTierLockEligibleProductIds
                                    }
                                    quoteOffering={quoteOffering}
                                  />
                                </MBox>
                              )}
                          </Card.Body>
                        </MCard>
                      </MTabPanel>

                      {/* PRODUCTS */}
                      {areProductsOptional && (
                        <MTabPanel>
                          <ManageProductsFormWithCard
                            disabled={disabled}
                            isSource="drawer"
                            offering={offering}
                            quoteOffering={quoteOffering}
                            updateOfferingItems={updateOfferingItems}
                            onClose={() => {
                              handleClose(false, false);
                            }}
                            closeDrawer={onClose}
                            setIsProductListChanged={setIsProductListChanged}
                            setProductsLoading={setProductsLoading}
                            isProductsLoading={isProductsLoading}
                            disableOneTimeProducts={disableOneTimeProducts}
                          />
                        </MTabPanel>
                      )}

                      {/* PRICE TIER SETTINGS */}
                      <MTabPanel>
                        {isPriceTierLoading ? (
                          <MFlex justify="center" grow={1} p={4}>
                            <MPageLoader height="200px" />
                          </MFlex>
                        ) : (
                          <QuotePriceTierDisplaySettings
                            isSaving={
                              isSavingPriceTier || isQuoteOfferingProcessing
                            }
                            quoteOffering={quoteOffering}
                            offeringType={offeringType}
                            currency={offeringRate?.currency || ''}
                            productsWithPrices={selectedProductsWithPrices}
                            productsObj={productsObj}
                            isPriceTierUpdated={isPriceTierUpdated}
                            savePriceTiers={savePriceTiers}
                            setIsPriceTierUpdated={setIsPriceTierUpdated}
                            setIsLoading={setIsPriceTierLoading}
                            setIsSaving={setIsSavingPriceTier}
                            disabled={disabled}
                          />
                        )}
                      </MTabPanel>
                    </MTabPanels>
                  </MTabs>
                </MDrawerBody>
              </MDrawerContent>
            </MDrawerPositioner>
          </Portal>
        </MDrawer>
      )}
    </MBox>
  );
};
